import React, { Component } from "react";
import axios from "../../utils/axiosInstance";
import Swal from "sweetalert2";

class ExpenseCategories extends Component {
  state = {
    categories: [],
    formData: { id: null, category_name: "", category_status: "", category_img: null },
    imagePreview: "",
    deleteId: null,
    error: "",
    loading: false,
    submitting: false,
    modalMode: "add",
  };

  componentDidMount() {
    this.fetchCategories();

    const modalEl = document.getElementById("categoryModal");
    if (modalEl) {
      modalEl.addEventListener("hidden.bs.modal", () => {
        this.setState({
          formData: { id: null, category_name: "", category_status: "", category_img: null },
          imagePreview: "",
          error: "",
          submitting: false,
          modalMode: "add",
        });
      });
    }
  }

  /** ✅ Fetch all categories */
  fetchCategories = async () => {
    this.setState({ loading: true, error: "" });
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get("/expenses/categories", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = res.data?.data || [];
      this.setState({ categories: Array.isArray(data) ? data : [] });
    } catch (err) {
      this.setState({
        error: err.response?.data?.message || "Failed to load categories.",
      });
    } finally {
      this.setState({ loading: false });
    }
  };

  /** ✅ Handle text input */
  handleChange = (e) => {
    const { name, value } = e.target;
    this.setState((prev) => ({
      formData: { ...prev.formData, [name]: value },
    }));
  };

  /** ✅ Handle file input */
  handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      this.setState((prev) => ({
        formData: { ...prev.formData, category_img: file },
        imagePreview: URL.createObjectURL(file),
      }));
    }
  };

  /** ✅ Open modal */
  openModal = (mode = "add", category = null) => {
    const modal = new window.bootstrap.Modal(document.getElementById("categoryModal"));
    const baseUrl = axios.defaults.baseURL.replace("/api", "");

    if (mode === "edit" && category) {
      this.setState({
        modalMode: "edit",
        formData: {
          id: category.id,
          category_name: category.category_name,
          category_status: category.category_status?.toString() ?? "1",
          category_img: null,
        },
        imagePreview: category.category_img
          ? `${baseUrl}/uploads/expense_categories/${category.category_img}`
          : "",
        error: "",
      });
    } else {
      this.setState({
        modalMode: "add",
        formData: { id: null, category_name: "", category_status: "", category_img: null },
        imagePreview: "",
        error: "",
      });
    }

    modal.show();
  };

  /** ✅ Submit form */
  handleSubmit = async (e) => {
    e.preventDefault();
    const { formData, modalMode } = this.state;

    if (!formData.category_name) {
      return this.setState({ error: "Category name is required." });
    }
    if (formData.category_status === "") {
      return this.setState({ error: "Category status is required." });
    }

    const data = new FormData();
    data.append("category_name", formData.category_name);
    data.append("category_status", formData.category_status);
    if (formData.category_img) data.append("category_img", formData.category_img);

    this.setState({ submitting: true, error: "" });

    try {
      const token = localStorage.getItem("token");

      if (modalMode === "add") {
        await axios.post("/expenses/categories", data, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        });
      } else {
        await axios.post(`/expenses/categories/${formData.id}?_method=PUT`, data, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        });
      }

      await this.fetchCategories();

      const modal = window.bootstrap.Modal.getInstance(document.getElementById("categoryModal"));
      modal.hide();

      Swal.fire({
        icon: "success",
        title: modalMode === "add" ? "Category added successfully!" : "Category updated successfully!",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (err) {
      console.error(err);
      this.setState({
        error: err.response?.data?.message || err.response?.data?.error || "Error saving category.",
      });
    } finally {
      this.setState({ submitting: false });
    }
  };

  /** ✅ Confirm delete */
  confirmDelete = (id) => {
    this.setState({ deleteId: id });
    const modal = new window.bootstrap.Modal(document.getElementById("deleteModal"));
    modal.show();
  };

  /** ✅ Handle delete */
  handleDelete = async () => {
    const { deleteId } = this.state;
    if (!deleteId) return;

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`/expenses/categories/${deleteId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await this.fetchCategories();

      const modal = window.bootstrap.Modal.getInstance(document.getElementById("deleteModal"));
      modal.hide();

      Swal.fire({
        icon: "success",
        title: "Category deleted successfully!",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch {
      Swal.fire({ icon: "error", title: "Failed to delete category." });
    }
  };

  render() {
    const { categories, formData, imagePreview, error, loading, submitting, modalMode } = this.state;
    const baseUrl = axios.defaults.baseURL.replace("/api", "");

    return (
      <div className="pc-container">
        <div className="pc-content">
          <div className="row">
            <div className="col-lg-12">
              <div className="card shadow-none">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <ul className="breadcrumb mb-0">
                    <li className="breadcrumb-item">Expense</li>
                    <li className="breadcrumb-item active">Categories</li>
                  </ul>
                  <button className="btn btn-light-warning" onClick={() => this.openModal("add")}>
                    + New Category
                  </button>
                </div>

                <div className="card-body">
                  {error && <div className="alert alert-danger">{error}</div>}
                  {loading ? (
                    <div className="text-center py-4">Loading...</div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-bordered table-striped">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Name</th>
                            <th>Image</th>
                            <th>Status</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {categories.length ? (
                            categories.map((cat, i) => (
                              <tr key={cat.id}>
                                <td>{i + 1}</td>
                                <td>{cat.category_name}</td>
                                <td>
                                  {cat.category_img ? (
                                    <img
                                      src={`${baseUrl}/uploads/expense_categories/${cat.category_img}`}
                                      alt="img"
                                      style={{ width: "50px", height: "50px", borderRadius: "6px" }}
                                    />
                                  ) : (
                                    "No Image"
                                  )}
                                </td>
                                <td>
                                  {cat.category_status === "1" || cat.category_status === 1 ? (
                                    <span className="badge bg-success">Active</span>
                                  ) : (
                                    <span className="badge bg-danger">Inactive</span>
                                  )}
                                </td>
                                <td>
                                  <button
                                    className="btn btn-sm btn-light-success me-2"
                                    onClick={() => this.openModal("edit", cat)}
                                  >
                                    <i className="ph ph-note-pencil"></i>
                                  </button>
                                  <button
                                    className="btn btn-sm btn-light-danger"
                                    onClick={() => this.confirmDelete(cat.id)}
                                  >
                                    <i className="ph ph-trash"></i>
                                  </button>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="5" className="text-center">
                                No categories found.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ✅ Add/Edit Modal */}
        <div className="modal fade" id="categoryModal" tabIndex={-1}>
          <div className="modal-dialog">
            <div className="modal-content">
              <form onSubmit={this.handleSubmit}>
                <div className="modal-header">
                  <h5 className="modal-title">
                    {modalMode === "add" ? "Add Category" : "Edit Category"}
                  </h5>
                  <button type="button" className="btn-close" data-bs-dismiss="modal"></button>
                </div>

                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Category Name</label>
                    <input
                      type="text"
                      name="category_name"
                      className="form-control"
                      value={formData.category_name}
                      onChange={this.handleChange}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Status</label>
                    <select
                      name="category_status"
                      className="form-select"
                      value={formData.category_status}
                      onChange={this.handleChange}
                      required
                    >
                      <option value="">Select Status</option>
                      <option value="1">Active</option>
                      <option value="0">Inactive</option>
                    </select>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Category Image</label>
                    <input
                      type="file"
                      className="form-control"
                      accept="image/*"
                      onChange={this.handleFileChange}
                    />
                    {imagePreview && (
                      <div className="mt-2 text-center">
                        <img
                          src={imagePreview}
                          alt="Preview"
                          style={{ width: "100px", borderRadius: "8px" }}
                        />
                      </div>
                    )}
                  </div>

                  {error && <div className="alert alert-danger">{error}</div>}
                </div>

                <div className="modal-footer">
                  <button type="button" className="btn btn-light-danger" data-bs-dismiss="modal">
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-light-primary" disabled={submitting}>
                    {submitting ? "Saving..." : modalMode === "add" ? "Add" : "Update"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* ✅ Delete Confirmation */}
        <div className="modal fade" id="deleteModal" tabIndex={-1}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-danger">
              <div className="modal-body text-center p-4">
                <i className="ph ph-warning text-danger f-40 mb-3"></i>
                <h5>Are you sure?</h5>
                <p className="text-muted mb-4">This action cannot be undone.</p>
                <div className="d-flex justify-content-center gap-3">
                  <button
                    type="button"
                    className="btn btn-light-secondary"
                    data-bs-dismiss="modal"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-light-danger"
                    onClick={this.handleDelete}
                  >
                    Yes, Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default ExpenseCategories;
